import { Navigate, useLocation } from 'react-router';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { RootState } from 'src/redux/store';
import { useAppSelector } from 'src/hook/hooks';
import { DashboardLayout } from 'src/layouts/dashboard';

import { usePremissionContext } from './use-permissions-context';

export const PermissionWrapper = ({
  module,
  children,
}: {
  module: string | string[];
  children: React.ReactNode;
}) => {
  const { hasPermission, permissions } = usePremissionContext();
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const location = useLocation();
  const { t } = useTranslate('translation');

  // 🔹 Optional loader if permissions are not loaded yet
  if ((loginApiData as any)?.tenant?.id && !permissions) {
    return (
      <DashboardLayout>
        <Box
          sx={{
            height: '90vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={48} />
            <Typography variant="h6">{t('LOADING')}</Typography>
          </Stack>
        </Box>
      </DashboardLayout>
    );
  }

  // 🔹 Special case: allow `/setting/billing` if billing.view exists
  // Check if billing.view is the only permission
  const isOnlyBilling =
    permissions?.billing?.view &&
    Object.keys(permissions)?.every(
      (key) => key === 'billing' || !Object.values(permissions[key] || {}).some((v) => v === true)
    );

  if (location?.pathname === '/dashboard' && isOnlyBilling && (loginApiData as any)?.tenant?.id) {
    return <Navigate to="/setting/billing" replace />;
  }

  // 🔐 Module permission check
  const hasModulePermission =
    typeof module === 'string'
      ? hasPermission(module)
      : Array.isArray(module)
        ? module.every((m) => hasPermission(m))
        : false;

  if (!hasModulePermission) {
    return <Navigate to="/403" replace />;
  }

  // ✅ Render children if allowed
  return <>{children}</>;
};
